import { Suspense, useEffect, useLayoutEffect } from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { FooterComponent } from "./components/footer";
import { ContactModal } from "./components/contactModal";
import {
  HomeNavigationComponent,
  NavigationComponent,
} from "./components/navigation";
import { StoreContextProvider } from "./store/store-context";
import { StyleProvider } from "./theme/provider";
import "bootstrap/dist/css/bootstrap.min.css";
import { ReactNotifications } from "react-notifications-component";
import { PageLoader } from "./components/loaders/pageloader.component";
import "./index.css";
import ContactUsProvider from "./providers/contactUs.provider";
import {
  BlogContentPage,
  BlogPage,
  CreateAccountPage,
  CreateFarmAccountPage,
  LazyComingSoonPage,
  LazyPrivacyPolicy,
  LazyTermsAndConditions,
  OurFarmPopUp,
  ShoppingCartPage,
  OurFarmsPage,
  ResetPasswordPage,
  SignInPage,
  SplitBuyDetailsPage,
  SplitBuyDealsPage,
  MarketPricesPage,
  CommodityPricesPage,
  AboutUsPage,
  SettingsPage,
  SettingsSplitBuyHistoryPage,
  SettingsAddressManagementPage,
  SettingsPasswordChangePage,
  SettingsPersonalInformationPage,
  SettingsReferAndEarnPage,
  VerifyResetPasswordPage,
  CheckoutPage,
  GuestPage,
  OrderCompletePage,
  BusinessesPage,
  BusinessLandingPage,
  LazyRefundPolicy, SplitBuyDealsSearchPage,
  AdminDashboardPage,
  AdminMarketingDashboardPage,
  AdminOpsOrdersPage,
  AdminOpsOrdersUsersPage,
  AdminOrderRatingsPage,
  AdminSplitBuyDetailsPage,
  AdminCommunicationsPage,
  AdminBroadcastHistoryPage,
  AdminMarketingBuyersPage,
  AdminBuyerDetailsPage,
  AdminFarmsDirectoryPage,
  AdminOrderTransactionPage,
  AdminMarketPricesPage,
  AdminMarketPriceHistoryPage,
  AdminSplitBuyPage,
  AdminLoginPage,
  RateOrderPage,
  EmailPreferencesPage,
} from "./routes";
import { AuthLayout } from "./layouts/auth";
import {
  ProtectedForCartRouteProvider,
  ProtectedRouteProvider,
} from "./providers/protected-route.provider";
import { AuthenticationProvider } from "./providers/authentication.provider";
import { CartPage } from "./pages/shoppingCart";
import "leaflet/dist/leaflet.css";
import { CartProvider } from "./providers/cart.provider";
import { GoogleMapsProvider } from "./providers/google-map.provider";
import { useLocation } from "react-router";
import { AdminLayout } from "./layouts/admin";
import {
  ADMIN_PERMISSIONS,
  AdminAuthenticationProvider,
  AdminLoginRoute,
  ProtectedAdminRoute,
  AdminDashboardRedirect,
  RequireAdminRole,
} from "./providers/adminAuthentication.provider";
import { activateAdminPageState } from "./utils/adminPageState";

export const RedirectTo = ({ redirectTo }) => (
  <Navigate to={redirectTo} replace />
);

const ScrollToTop = () => {
  const { pathname } = useLocation();

  useLayoutEffect(() => {
    activateAdminPageState(pathname);

    if ("scrollRestoration" in window.history) {
      window.history.scrollRestoration = "manual";
    }

    const resetScroll = () => {
      window.scrollTo({ top: 0, left: 0, behavior: "auto" });
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    };

    resetScroll();
    const frame = window.requestAnimationFrame(resetScroll);
    const adminContent = document.getElementById("admin-main-content");
    if (adminContent) adminContent.scrollTop = 0;

    return () => window.cancelAnimationFrame(frame);
  }, [pathname]);

  return null;
};

const NavigationWrapper = () => {
  const location = useLocation();
  const isAdminPage = location.pathname.startsWith("/admin/internal");
  if (isAdminPage || ["/rate-order", "/create-farm-account"].includes(location.pathname)) return null;
  const isHomePage = [
    "/",
    "/blog",
    "/farms",
    "/refund-policy",
    "/privacy-policy",
    "/businesses",
    "/business",
    "/terms-and-conditions",
    "/market-prices",
    "/market-prices/commodities",
    "/about-us",
  ].includes(location.pathname);

  return isHomePage ? <HomeNavigationComponent /> : <NavigationComponent />;
};

const FooterWrapper = () => {
  const location = useLocation();
  return location.pathname.startsWith("/admin/internal") || ["/rate-order", "/create-farm-account"].includes(location.pathname) ? null : <FooterComponent />;
};

function App() {
  return (
    <StoreContextProvider>
      <ReactNotifications />
      <Suspense fallback={<PageLoader />}>
        <GoogleMapsProvider>
          <StyleProvider>
            <BrowserRouter>
              <ScrollToTop />
              <AuthenticationProvider>
                <CartProvider>
                  <ContactUsProvider>
                    <NavigationWrapper />
                    <main>
                      <Routes>
                        <Route path="/">
                          <Route index element={<LazyComingSoonPage />} />
                          <Route path="split-buy" element={<SplitBuyDealsPage />}>
                            <Route path="details/:split_buy_id" element={<SplitBuyDetailsPage />} />
                          </Route>
                          <Route path="market-prices" element={<MarketPricesPage />} />
                          <Route path="market-prices/commodities" element={<CommodityPricesPage />} />
                          <Route path="about-us" element={<AboutUsPage />} />
                          <Route path="split-buy/search"  element={<SplitBuyDealsSearchPage />}   >
                            <Route  path="details/:split_buy_id" element={<SplitBuyDetailsPage />} />
                          </Route>
                          {/* <Route path="businesses"  element={<BusinessesPage />} /> */}
                          <Route path="businesses" element={<BusinessLandingPage />} />
                          <Route path="privacy-policy"  element={<LazyPrivacyPolicy />} />
                          <Route path="refund-policy" element={<LazyRefundPolicy />} />
                          <Route path="terms-and-conditions"  element={<LazyTermsAndConditions />}  />
                          <Route path="farms" element={<OurFarmsPage />}>
                            <Route path=":farm_slug" element={<OurFarmPopUp />}/>
                          </Route>
                          <Route path="notifications/preferences" element={<EmailPreferencesPage />} />
                          <Route path="blog" element={<BlogPage />}></Route>
                          <Route path="create-farm-account" element={<CreateFarmAccountPage />} />
                          <Route path="notification/preferences" element={<EmailPreferencesPage />} />
                          <Route path="notifications/preferences" element={<EmailPreferencesPage />} />
                          <Route
                            path="blog/:blogSection/:blogTopic"
                            element={<BlogContentPage />}
                          />
                          <Route element={<ProtectedRouteProvider />}>
                            <Route path={"settings"} element={<SettingsPage />}>
                              <Route
                                index
                                element={
                                  <Navigate replace to={"split-buy-history"} />
                                }
                              ></Route>
                              <Route
                                element={<SettingsSplitBuyHistoryPage />}
                                path={"split-buy-history"}
                              />
                              <Route
                                element={<SettingsAddressManagementPage />}
                                path={"address-management"}
                              />
                              <Route
                                element={<SettingsPasswordChangePage />}
                                path={"change-password"}
                              />
                              <Route
                                element={<SettingsPersonalInformationPage />}
                                path={"personal-information"}
                              />
                              <Route
                                element={<SettingsReferAndEarnPage />}
                                path={"refer-and-earn"}
                              />
                              <Route
                                element={<EmailPreferencesPage />}
                                path={"email-preferences"}
                              />
                            </Route>
                          </Route>
                          <Route path={"cart"} element={<CartPage />}>
                            <Route index element={<ShoppingCartPage />}></Route>
                            <Route element={<ProtectedForCartRouteProvider />}>
                              <Route
                                path={"checkout"}
                                element={<CheckoutPage />}
                              ></Route>
                              <Route
                                path={"checkout/staff"}
                                element={<CheckoutPage />}
                              ></Route>
                              <Route
                                path={"order-status"}
                                element={<OrderCompletePage />}
                              ></Route>
                            </Route>
                          </Route>
                        </Route>
                        <Route element={<AuthLayout />}>
                          <Route
                            path={"guest-sign-in"}
                            element={<GuestPage />}
                          />
                          <Route path={"sign-in"} element={<SignInPage />} />
                          <Route
                            path={"create-account"}
                            element={<CreateAccountPage />}
                          />
                          <Route
                            path={"reset-password"}
                            element={<ResetPasswordPage />}
                          />
                          <Route
                            path={"set-new-password"}
                            element={<VerifyResetPasswordPage />}
                          />
                        </Route>
                        <Route path="rate-order" element={<RateOrderPage />} />
                        <Route path="admin/internal">
                          <Route element={<AdminAuthenticationProvider />}>
                            <Route element={<AdminLoginRoute />}>
                              <Route path="login" element={<AdminLoginPage />} />
                            </Route>
                            <Route element={<ProtectedAdminRoute />}>
                              <Route element={<AdminLayout />}>
                                <Route
                                  index
                                  element={<Navigate replace to="dashboard" />}
                                />
                                <Route path="dashboard">
                                  <Route index element={<AdminDashboardRedirect />} />
                                  <Route path="operations" element={<AdminDashboardPage />} />
                                  <Route path="overall" element={<Navigate replace to="/admin/internal/dashboard/operations" />} />
                                  <Route path="ops" element={<Navigate replace to="/admin/internal/dashboard/operations" />} />
                                  <Route path="marketing" element={<AdminMarketingDashboardPage />} />
                                </Route>
                                <Route path="resources">
                                  <Route element={<RequireAdminRole allowedRoles={ADMIN_PERMISSIONS.OPERATIONS} />}>
                                    <Route path="split-buy" element={<AdminSplitBuyPage />} />
                                    <Route path="split-buy/:splitBuyId" element={<AdminSplitBuyDetailsPage />} />
                                  </Route>
                                </Route>
                                <Route path="operations">
                                  <Route element={<RequireAdminRole allowedRoles={ADMIN_PERMISSIONS.OPERATIONS} />}>
                                    <Route path="orders" element={<AdminOpsOrdersPage />} />
                                    <Route path="orders/users" element={<AdminOpsOrdersUsersPage />} />
                                    <Route path="orders/ratings" element={<AdminOrderRatingsPage />} />
                                    <Route path="orders/:orderId" element={<AdminOrderTransactionPage />} />
                                    <Route path="market-prices" element={<AdminMarketPricesPage />} />
                                    <Route path="market-prices/edit/:id" element={<AdminMarketPricesPage />} />
                                    <Route path="market-prices/history" element={<AdminMarketPriceHistoryPage />} />
                                  </Route>
                                </Route>
                                <Route element={<RequireAdminRole allowedRoles={ADMIN_PERMISSIONS.CUSTOMERS} />}>
                                  <Route path="customers/registered" element={<AdminMarketingBuyersPage customerType="registered" />} />
                                  <Route path="customers/registered/:buyerId" element={<AdminBuyerDetailsPage customerType="registered" />} />
                                  <Route path="customers/guests" element={<AdminMarketingBuyersPage customerType="guest" />} />
                                  <Route path="customers/guests/:buyerId" element={<AdminBuyerDetailsPage customerType="guest" />} />
                                  <Route path="customers/farms" element={<AdminFarmsDirectoryPage />} />
                                  <Route path="buyers" element={<Navigate replace to="/admin/internal/customers/registered" />} />
                                  <Route path="buyers/:buyerId" element={<AdminBuyerDetailsPage customerType="registered" />} />
                                </Route>
                                <Route path="marketing">
                                  <Route element={<RequireAdminRole allowedRoles={ADMIN_PERMISSIONS.MARKETING} />}>
                                    <Route path="buyers" element={<Navigate replace to="/admin/internal/customers/registered" />} />
                                    <Route path="broadcast" element={<AdminCommunicationsPage />} />
                                    <Route path="broadcast/history" element={<AdminBroadcastHistoryPage />} />
                                    <Route path="communications" element={<Navigate replace to="/admin/internal/marketing/broadcast" />} />
                                  </Route>
                                </Route>
                              </Route>
                            </Route>
                          </Route>
                        </Route>
                        <Route
                          path="*"
                          element={<RedirectTo redirectTo="/" />}
                        />
                      </Routes>
                    </main>
                    <ContactModal />
                    <FooterWrapper />
                  </ContactUsProvider>
                </CartProvider>
              </AuthenticationProvider>
            </BrowserRouter>
          </StyleProvider>
        </GoogleMapsProvider>
      </Suspense>
    </StoreContextProvider>
  );
}

export default App;
